import useModalStack from 'contexts/modal/useModalStack';
import { DayDrivingTime } from './useDayDrivingTimes';
import ConfirmModal from 'components/Modal/ConfirmModal';
import { useAssignmentFindUnassignedAssignmentsForUserBetweenZipCodesOnDate } from 'api/assignment/assignment';
import TextButton from 'components/inputs/TextButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import useUsers from 'contexts/basicData/useUsers';
import { useMemo } from 'react';
import {
  formatTime,
  getHoursAndMinutesFromMillisecondsString,
} from 'utils/date-helpers';
import useTranslations from 'contexts/basicData/useTranslations';
import styled from 'styled-components';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import { useUserGetDrivingPolicyReportForUnPlannedAssignmentHook } from 'api/user/user';
import { AssignmentModel, CaseModel, DrivingPolicyReportDto } from 'api/model';
import {
  makeDateComparator,
  makeNumberComparator,
  makeStringComparator,
} from 'utils/sorting';
import {
  getTimeLeftIncludingNewAssignment,
  // getWorkTimeIncludingNewAssignment,
} from './worktime-helper';
import goToCase, {
  getAssignmentPriority,
  getAssignmentPriorityIcon,
} from 'utils/case-utils';
import useCompanies from 'contexts/basicData/useCompanies';
import AddressAndTimePill from 'components/Address/AddressAndTimePill';

const Span = styled.span`
  white-space: wrap;
`;

const InvalidRowClassName = 'invalid-row';

const TableStylerWrapper = styled.div`
  display: contents;

  .${InvalidRowClassName} {
    color: ${({ theme }) => theme.colors.foreground.error};
  }
`;

export enum PlaceholderAssignmentTypeEnum {
  PersonalTransport,
  CarPlaceholder,
}

type AssignmentViewModelWithDrivingPolicy = {
  drivingPolicy?: DrivingPolicyReportDto;
  assignment: AssignmentModel;
  case: CaseModel;
};

type Props = {
  drivingTimes?: DayDrivingTime[];
  userId: number;
};

export default function useFindSuitableAssignments({
  userId,
  drivingTimes = [],
}: Props) {
  const { caseTypes, assignmentTypes } = useTranslations();
  const users = useUsers();
  const { companiesByAddrDict } = useCompanies();

  const user = useMemo(() => {
    return users[userId];
  }, [users, userId]);

  const modalStack = useModalStack();

  // API
  const getDrivingPolicyForUplannedAssignmentCall =
    useUserGetDrivingPolicyReportForUnPlannedAssignmentHook();
  const findUnassignedAssignmentsForUserBetweenZipCodesOnDateCall =
    useAssignmentFindUnassignedAssignmentsForUserBetweenZipCodesOnDate();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getDrivingPolicyForUplannedAssignment = async (
    assignmentModel: AssignmentModel
  ) => {
    const usedStartTime = assignmentModel.estimatedStartTime;
    if (!usedStartTime) {
      return undefined;
    }
    // Laddbufferten (elbil) är belagd tid och ingår i det fönster körtidskollen prövar.
    const estimatedDuration =
      (assignmentModel.estimatedDuration ?? 0) +
      (assignmentModel.electricCarBufferTime ?? 0);
    const usedEndTime = new Date(usedStartTime.getTime() + estimatedDuration);
    const drivingPolicy = await getDrivingPolicyForUplannedAssignmentCall({
      userId: assignmentModel.assignedTo!.userID!,
      bookedTo: assignmentModel.bookedTo,
      startOrEstimatedStartTime: usedStartTime,
      endOrEstimatedEndTime: usedEndTime,
      estimatedDuration,
    });
    return drivingPolicy;
  };

  const columnSettings = useMemo((): ColumnSetting<
    AssignmentViewModelWithDrivingPolicy,
    any
  >[] => {
    return [
      {
        head: 'Prio',
        render: (a) => getAssignmentPriorityIcon(getAssignmentPriority(a)),
        width: 50,
        sortFunction: makeNumberComparator((a) => getAssignmentPriority(a)),
      },
      {
        head: 'Skapat',
        render: (a) => formatTime(a.assignment.created),
        width: 70,
        sortFunction: makeDateComparator(
          (a) => a.assignment.created ?? new Date()
        ),
      },
      {
        head: 'Regnr',
        render: (a) => a.case.registrationNumber,
        width: 100,
        sortFunction: makeStringComparator((a) => a.case.registrationNumber),
      },
      {
        head: 'Bokat till',
        render: (a) => {
          return formatTime(a.assignment.bookedTo);
        },
        width: 70,
        sortFunction: makeDateComparator((a) => a.assignment.bookedTo),
      },
      {
        head: 'Estimerad tid',
        render: (a) =>
          a.assignment.estimatedDuration
            ? getHoursAndMinutesFromMillisecondsString(
                a.assignment.estimatedDuration
              )
            : '–',
        width: 100,
        sortFunction: makeNumberComparator(
          (a) => a.assignment.estimatedDuration ?? 0
        ),
      },
      // {
      //   head: 'Total tid inkl uppdraget',
      //   render: (a) => {
      //     const workTime = getWorkTimeIncludingNewAssignment(
      //       a.assignment,
      //       0 /** TODO: CALCULATE WORK HOURS */
      //     );
      //     return workTime
      //       ? getHoursAndMinutesFromMillisecondsString(workTime)
      //       : '–';
      //   },
      //   width: 200,
      //   sortFunction: makeNumberComparator(
      //     (a) =>
      //       getTimeLeftIncludingNewAssignment(
      //         a.assignment,
      //         0 /** TODO: CALCULATE CONSECUTIVE HOURS OF REST 24 H */,
      //         0 /** TODO: CALCULATE WORK HOURS */
      //       ) ?? 0
      //   ),
      // },
      // {
      //   head: 'Tid kvar inkl uppdraget',
      //   render: (a) => {
      //     const timeLeft = getTimeLeftIncludingNewAssignment(
      //       a.assignment,
      //       0 /** TODO: CALCULATE CONSECUTIVE HOURS OF REST 24 H */,
      //       0 /** TODO: CALCULATE WORK HOURS */
      //     );
      //     return timeLeft
      //       ? getHoursAndMinutesFromMillisecondsString(timeLeft)
      //       : '-';
      //   },
      //   width: 200,
      //   sortFunction: makeNumberComparator(
      //     (a) =>
      //       getTimeLeftIncludingNewAssignment(
      //         a.assignment,
      //         0 /** TODO: CALCULATE CONSECUTIVE HOURS OF REST 24 H */,
      //         0 /** TODO: CALCULATE WORK HOURS */
      //       ) ?? 0
      //   ),
      // },
      {
        head: 'Ärendetyp',
        render: (a) => caseTypes[a.case.caseTypeID],
        width: 100,
        sortFunction: makeStringComparator(
          (a) => caseTypes[a.case.caseTypeID] ?? ''
        ),
      },
      {
        head: 'Uppdragstyp',
        render: (a) => assignmentTypes[a.assignment.assignmentTypeID],
        width: 150,
        sortFunction: makeStringComparator(
          (a) => assignmentTypes[a.assignment.assignmentTypeID] ?? ''
        ),
      },
      {
        head: 'Från',
        render: (a) => {
          const addressKey =
            `${a.assignment.fromAddress}${a.assignment.fromZip}${a.assignment.fromCity}`
              .toLowerCase()
              .replaceAll(' ', '');
          const addressString =
            companiesByAddrDict.get(addressKey)?.name ??
            `${a.assignment.fromAddress}, ${a.assignment.fromZip}, ${a.assignment.fromCity}`;
          return <Span>{addressString}</Span>;
        },
        width: 200,
        sortFunction: makeStringComparator((a) => a.assignment.fromAddress),
      },
      {
        head: 'Till',
        render: (a) => {
          const addressKey =
            `${a.assignment.toAddress}${a.assignment.toZip}${a.assignment.toCity}`
              .toLowerCase()
              .replaceAll(' ', '');
          const addressString =
            companiesByAddrDict.get(addressKey)?.name ??
            `${a.assignment.toAddress}, ${a.assignment.toZip}, ${a.assignment.toCity}`;
          return <Span>{addressString}</Span>;
        },
        width: 200,
        sortFunction: makeStringComparator((a) => a.assignment.toAddress),
      },
      // {
      //   head: 'Bryter körtidspolicy',
      //   render: (a) => {
      //     if (!a.drivingPolicy) {
      //       return '-';
      //     }
      //     return (
      //       <>
      //         <div
      //           style={{
      //             color: a.drivingPolicy.canAdd ? 'green' : 'red',
      //           }}
      //         >
      //           {a.drivingPolicy.canAdd ? 'Nej' : 'Ja'}
      //         </div>
      //         {!a.drivingPolicy.hasConsecutiveHoursOfRestLast24Hours && (
      //           <span>24h</span>
      //         )}
      //         {!a.drivingPolicy.hasConsecutiveHoursOfRestThisWeek && (
      //           <span>Vecka</span>
      //         )}
      //       </>
      //     );
      //   },
      //   width: 200,
      // },
      // {
      //   head: '',
      //   render: (a) => (
      //     <TextButton
      //       onClick={(e) => {
      //         e.stopPropagation();
      //         e.preventDefault();
      //         openGoogleMapsDirections({
      //           fromAddress: assignmentModel.toAddress,
      //           fromCity: assignmentModel.toCity,
      //           fromZip: assignmentModel.toZip,
      //           toAddress: a.assignment.fromAddress,
      //           toCity: a.assignment.fromCity,
      //           toZip: a.assignment.fromZip,
      //         });
      //       }}
      //     >
      //       <img
      //         alt="Google maps icon"
      //         height={15}
      //         src="/images/googlemapsiconsmall.png"
      //         width={15}
      //       />
      //     </TextButton>
      //   ),
      //   width: 40,
      // },
      {
        head: '',
        render: (a) => (
          <TextButton onClick={() => goToCase(a.case.caseID)}>
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </TextButton>
        ),
        width: 40,
      },
      // {
      //   head: '',
      //   render: (a) => (
      //     <ReturnAssignmentActionDropdown
      //       currentAssignment={{
      //         assignment: assignmentModel,
      //         case: caseModel,
      //       }}
      //       selectedAssignment={{
      //         assignment: a.assignment,
      //         case: a.case,
      //       }}
      //       onUpdated={() => {
      //         searchAssignments();
      //       }}
      //     />
      //   ),
      //   width: 40,
      // },
    ];
  }, [assignmentTypes, caseTypes, companiesByAddrDict]);

  const handleFindSuitableAssignments = async ({
    fromAssignment,
    toAssignment,
  }: {
    fromAssignment?: DayDrivingTime;
    toAssignment?: DayDrivingTime;
  }) => {
    const fromZipCode = (
      fromAssignment?.toAddress.zip ?? user?.postalCode
    )?.substring(0, 2);
    const toZipCode = (
      toAssignment?.fromAddress.zip ?? user?.postalCode
    )?.substring(0, 2);
    const fromDate = fromAssignment?.end;
    const toDate = toAssignment?.start;

    const assignments =
      await findUnassignedAssignmentsForUserBetweenZipCodesOnDateCall.mutateAsync(
        {
          params: {
            userId,
            fromZipCode,
            toZipCode,
            startSpan: fromDate,
            endSpan: toDate,
          },
        }
      );

    modalStack.push(
      <ConfirmModal
        title={`Passande uppdrag för ${user?.name} mellan 
          ${fromDate ? formatTime(fromDate) : '-'} och 
          ${toDate ? formatTime(toDate) : '-'}`}
        onCancel={() => modalStack.pop()}
      >
        <div style={{ whiteSpace: 'wrap', lineHeight: '25px' }}>
          Behöver ansluta{' '}
          {fromAssignment && (
            <p>
              från{' '}
              {fromAssignment.assignment?.case.registrationNumber ??
                fromAssignment.type}{' '}
              som slutar på{' '}
              <AddressAndTimePill
                address={fromAssignment.toAddress}
                time={fromAssignment.end}
              />
            </p>
          )}
          {toAssignment && (
            <p>
              till{' '}
              {toAssignment.assignment?.case.registrationNumber ??
                toAssignment.type}{' '}
              som börjar på{' '}
              <AddressAndTimePill
                address={toAssignment.fromAddress}
                time={toAssignment.start}
              />
            </p>
          )}
        </div>
        <TableStylerWrapper>
          <Table
            rows={assignments}
            // onRowClick={(row) => goToCase(row.case.caseID)}
            columnSettings={columnSettings}
            initialSortState={{
              ascending: false,
              sortedColumnIndex: 1,
            }}
            rowClassName={(row) => {
              return '';
              let rowClassName = '';
              if (row.assignment.estimatedDuration === null) {
                rowClassName = InvalidRowClassName;
              }
              const isWithinTime =
                (getTimeLeftIncludingNewAssignment(
                  row.assignment,
                  0 /** TODO: CALCULATE CONSECUTIVE HOURS OF REST 24 H */,
                  0 /** TODO: CALCULATE WORK HOURS */
                ) ?? 0) > 0;
              rowClassName = isWithinTime ? '' : InvalidRowClassName;

              return `${rowClassName} ${row.assignment.assignmentID}`;
            }}
          />
        </TableStylerWrapper>
        {/* {assignments?.map((a) => (
          <Row>
            <span>{a.caseRegistrationNumber}</span>
            <span>
              {caseTypes[a.caseType]}, {assignmentTypes[a.assignmentType]}
            </span>
            <TextButton
              onClick={() => {
                window.open(
                  `/sok/${a.caseID}`,
                  a.caseID.toString() ?? '_blank'
                );
              }}
              key={a.assignmentID}
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </TextButton>
          </Row>
        ))} */}
        {assignments?.length === 0 && <p>Inga passande uppdrag hittades.</p>}
      </ConfirmModal>
    );
  };

  const handleFindSuitableMorningAssignments = async () => {
    const firstAssignment = drivingTimes[0];

    await handleFindSuitableAssignments({
      fromAssignment: undefined,
      toAssignment: firstAssignment,
    });
  };

  const handleFindSuitableAfternoonReturnAssignments = async () => {
    const lastAssignment = drivingTimes[drivingTimes.length - 1];
    await handleFindSuitableAssignments({
      fromAssignment: lastAssignment,
      toAssignment: undefined,
    });
  };

  const handleFindSuitableMiddayAssignments = async (
    assignmentOneIndex: number,
    assignmentTwoIndex: number
  ) => {
    const firstAssignment = drivingTimes[assignmentOneIndex];
    const secondAssignment = drivingTimes[assignmentTwoIndex];

    await handleFindSuitableAssignments({
      fromAssignment: firstAssignment,
      toAssignment: secondAssignment,
    });
  };

  return {
    handleFindSuitableAssignments,
    handleFindSuitableMorningAssignments,
    handleFindSuitableAfternoonReturnAssignments,
    handleFindSuitableMiddayAssignments,
    loading:
      findUnassignedAssignmentsForUserBetweenZipCodesOnDateCall.isLoading,
  };
}
